import type { CartLine } from './otherSalePos';

/**
 * P-082 · el pedido del punto de venta sobrevive a salir de la pantalla y volver.
 *
 * El pedido vive en el estado del punto de venta: al ir a otra pantalla de la app (el menú, Atrás hasta
 * «Ventas e ingresos») se desmontaba y se perdía, y la venta que ya se había guardado para él quedaba en
 * «Ventas guardadas sin cobrar» (prueba del dueño, 2026-10-12). Ahora, al desmontarse, el punto de venta deja
 * aquí su pedido —las líneas, la tienda, la clave de intención y la venta que salió de él— y al volver lo
 * retoma: «Cobrar» manda la MISMA clave y el servidor devuelve la MISMA venta (es idempotente), así que no
 * nace otra, y mientras sea la del pedido no se lista como «otra» venta guardada (P-081).
 *
 *   · Solo en memoria: sobrevive a navegar dentro de la app, no a recargar ni a cerrar (entonces la venta
 *     guardada aparece en la lista, como antes). No se escribe en el almacenamiento del navegador.
 *   · Es de una persona en un negocio: con otra cuenta (o tras cerrar sesión) no se retoma.
 *   · No decide nada de dinero: el servidor sigue clasificando la venta que devuelve (cobrada, cancelada,
 *     con un cobro con tarjeta sin resolver) como siempre.
 */
export interface PosOrderMemory {
  /** De quién es (negocio y persona): `posOrderMemoryKey`. */
  key: string;
  storeId: string;
  cart: CartLine[];
  /** La clave de intención de venta del pedido, si ya se pidió abrir la venta. */
  idempotencyKey?: string;
  /** La venta que salió de este pedido, si ya existe. */
  cartSaleId?: string;
}

let remembered: PosOrderMemory | undefined;

/** De quién es el pedido: negocio y persona. Sin alguno de los dos no se recuerda nada. */
export function posOrderMemoryKey(businessId: string | undefined, userId: string | undefined): string | undefined {
  return businessId && userId ? `${businessId}.${userId}` : undefined;
}

/** Deja el pedido para cuando se vuelva. Un pedido vacío no se guarda: borra lo que hubiera. */
export function rememberPosOrder(order: PosOrderMemory): void {
  remembered = order.cart.length > 0 ? { ...order, cart: order.cart.map((line) => ({ ...line })) } : undefined;
}

/** El pedido que dejó ESTA persona en ESTE negocio, si hay. El de otra cuenta se descarta. */
export function recallPosOrder(key: string | undefined): PosOrderMemory | undefined {
  if (!remembered) return undefined;
  // Sin saber todavía quién es, no se decide nada (ni se borra).
  if (!key) return undefined;
  if (remembered.key !== key) { remembered = undefined; return undefined; }
  return { ...remembered, cart: remembered.cart.map((line) => ({ ...line })) };
}

/** Olvida el pedido recordado. */
export function forgetPosOrder(): void {
  remembered = undefined;
}
